import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Paper,
  Typography,
  Alert,
  Button,
  CircularProgress,
  Divider,
  Stack,
  IconButton,
  Tooltip
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import api from '../services/api';

const OrderComplete = () => {
  const { t } = useTranslation('order');
  const navigate = useNavigate();
  const { orderNo } = useParams();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  // 從 Checkout 傳來的付款資訊
  const paymentInfo = location.state?.paymentInfo;

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/orders/${orderNo}`);
        if (!mounted) return;
        if (res.data?.success === false) {
          setError(res.data?.error || 'Failed to load order.');
          return;
        }
        const o = res.data?.data || res.data?.order || res.data;
        setOrder(o);
      } catch (e) {
        if (!mounted) return;
        const status = e?.response?.status;
        if (status === 404) setError('Order not found.');
        else setError(e?.response?.data?.error || 'Failed to load order.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => { mounted = false; };
  }, [orderNo]);

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(''), 2000);
    });
  };

  const formatDeadline = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const renderPaymentInfo = () => {
    if (!paymentInfo) return null;

    // SmilePay ATM 虛擬帳號
    if (paymentInfo.type === 'atm') {
      return (
        <Alert severity="info" icon={<AccountBalanceIcon />} sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            ATM 轉帳資訊
          </Typography>
          <Stack spacing={0.5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">
                銀行：{paymentInfo.bankName || ''} ({paymentInfo.bankNo || ''})
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">
                虛擬帳號：<b>{paymentInfo.accountNo || ''}</b>
              </Typography>
              {paymentInfo.accountNo && (
                <Tooltip title={copied === 'account' ? '已複製' : '複製帳號'}>
                  <IconButton size="small" onClick={() => handleCopy(paymentInfo.accountNo, 'account')}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {paymentInfo.deadline && (
              <Typography variant="body2" color="error">
                繳款期限：{formatDeadline(paymentInfo.deadline)}
              </Typography>
            )}
          </Stack>
        </Alert>
      );
    }

    // 店家銀行帳號（人工對帳）
    if (paymentInfo.type === 'manual_atm') {
      return (
        <Alert severity="warning" icon={<AccountBalanceIcon />} sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            銀行轉帳資訊
          </Typography>
          <Stack spacing={0.5}>
            <Typography variant="body2">
              銀行：{paymentInfo.bankName || ''} {paymentInfo.bankCode ? `(${paymentInfo.bankCode})` : ''}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">
                帳號：<b>{paymentInfo.bankAccount || ''}</b>
              </Typography>
              {paymentInfo.bankAccount && (
                <Tooltip title={copied === 'account' ? '已複製' : '複製帳號'}>
                  <IconButton size="small" onClick={() => handleCopy(paymentInfo.bankAccount, 'account')}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Typography variant="body2">
              戶名：{paymentInfo.bankHolder || ''}
            </Typography>
            {paymentInfo.deadline && (
              <Typography variant="body2" color="error">
                請於 {formatDeadline(paymentInfo.deadline)} 前完成轉帳
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              轉帳後請聯繫店家告知您的匯款帳號後五碼，以便對帳
            </Typography>
          </Stack>
        </Alert>
      );
    }

    // CVS 超商條碼
    if (paymentInfo.type === 'cvs_barcode') {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            超商繳費資訊
          </Typography>
          <Stack spacing={0.5}>
            <Typography variant="body2">條碼 1：{paymentInfo.barcode1}</Typography>
            <Typography variant="body2">條碼 2：{paymentInfo.barcode2}</Typography>
            <Typography variant="body2">條碼 3：{paymentInfo.barcode3}</Typography>
            {paymentInfo.deadline && (
              <Typography variant="body2" color="error">
                繳費期限：{formatDeadline(paymentInfo.deadline)}
              </Typography>
            )}
          </Stack>
        </Alert>
      );
    }

    return null;
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: { xs: 2, md: 4 } }}>
      <Container maxWidth="sm">
        <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {t('complete.order_status')}
              </Typography>
              <Alert severity="warning" sx={{ mt: 2 }}>
                {error}
              </Alert>
              <Box sx={{ mt: 3, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button variant="outlined" onClick={() => navigate('/')}>{t('complete.back_home')}</Button>
                <Button variant="contained" onClick={() => navigate('/products')}>{t('complete.continue_shopping')}</Button>
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {t('complete.thank_you')}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('complete.order_no', { orderNo })}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body1">
                {t('complete.total')}: <b>NT$ {Number(order?.total_amount ?? order?.totalAmount ?? 0).toLocaleString()}</b>
              </Typography>

              {renderPaymentInfo()}

              <Box sx={{ mt: 3, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button variant="outlined" onClick={() => navigate(`/order/${orderNo}`)}>{t('complete.view_order')}</Button>
                <Button variant="contained" onClick={() => navigate('/products')}>{t('complete.continue_shopping')}</Button>
              </Box>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default OrderComplete;
